// Basic Calculator - Mobile Wizard Interface
// Accordion-style sections, one screen, easy to use on iPhone
// Completely separate from desktop version - won't break existing calculator

import React, { useState, useRef, useEffect } from 'react';
import { SimulationEngine } from '../SimulationEngine';
import { FinancialConfig } from '../FinancialConfig';
import CalculatorBridge from '../CalculatorBridge';
import { PDFExportManager } from '../PDFExportManager';
import { InfoTooltip } from './InfoTooltip';
import MobileLoadingSpinner from './MobileLoadingSpinner';
import { EmailCaptureForm } from './EmailCaptureForm';
import {
  buildBasicEmailResults,
  generateBasicCalculatorPdfBase64
} from '../utils/basicCalculatorPdfEmail.js';
import { SubscriptionManager } from '../SubscriptionManager';
import {
    trackFirstCalculatorInput,
    trackCalculationComplete,
    trackBasicAdvancedCtaShown
} from '../utils/funnelAnalytics';
import { openPricingModal } from '../utils/openPricingModal.js';
import { alertCheckoutFailure, startRecommendedCheckout } from '../utils/recommendedCheckout.js';
import CalculationTransparency from './CalculationTransparency.js';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import {
    buildBasicCalcContext,
    computeBasicMSI,
    computeBasicAchievableYearIncome,
    resolveBasicRetirementPeriodYears,
    runBasicLiveToAgeWhatIf,
    runBasicMoreIncomeWhatIf,
    runBasicAnnualFeesWhatIf,
    runBasicSequenceRiskWhatIf,
} from '../basicWhatIfEngine.js';

const DEFAULT_BASIC_MOBILE_INPUTS = {
    relationshipStatus: 'single',
    yourAge: '',
    yourGender: 'male',
    yourSuper: '',
    partnerAge: '',
    partnerGender: 'female',
    partnerSuper: '',
    retirementPeriod: '25',
    desiredAnnualIncome: '',
    otherTaxableIncome: '',
    otherNonTaxableIncome: '',
    assessableAssets: '',
    isHomeowner: true,
    includePension: true,
    assumedReturn: 6.5,
    assumedInflation: 2.5,
    adminFees: 52,
    adminFeePercentage: 0.1,
    investmentFees: 0.5,
    insuranceFees: 0,
    pensionGrowthRate: 0.3
};

const SCENARIO_PRESETS_MOB = {
    early:   { yourAge: 60, yourSuper: 400000, desiredAnnualIncome: '40000', retirementPeriod: '30' },
    typical: { yourAge: 65, yourSuper: 600000, desiredAnnualIncome: '55000', retirementPeriod: '25' },
    high:    { yourAge: 67, yourSuper: 900000, desiredAnnualIncome: '70000', retirementPeriod: '20' },
};

export const BasicCalculatorMobile = ({ onBack, onGoToAdvanced }) => {
    const resolveInitialMobileState = () => {
        const transferData = CalculatorBridge.retrieveTransferData('basic');
        if (transferData) {
            return { inputs: transferData, scenarioPreset: null };
        }
        if (SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) {
            try {
                const saved = localStorage.getItem('basicCalculatorMobile_inputs');
                if (saved) {
                    return { inputs: JSON.parse(saved), scenarioPreset: null };
                }
            } catch (error) {
                console.error('Error loading saved inputs:', error);
            }
            return { inputs: { ...DEFAULT_BASIC_MOBILE_INPUTS }, scenarioPreset: null };
        }
        return {
            inputs: { ...DEFAULT_BASIC_MOBILE_INPUTS, ...SCENARIO_PRESETS_MOB.typical },
            scenarioPreset: 'typical',
        };
    };

    const initialMobileState = resolveInitialMobileState();

    // Declare all state BEFORE using in useEffect
    const [expandedSection, setExpandedSection] = useState(0);
    const [hasCalculated, setHasCalculated] = useState(false);
    const [inputs, setInputs] = useState(initialMobileState.inputs);
    const [results, setResults] = useState(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [showDetailedChart, setShowDetailedChart] = useState(false);
    const [isCalculating, setIsCalculating] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [selectedScenarioPreset, setSelectedScenarioPreset] = useState(initialMobileState.scenarioPreset);
    const [selectedReturnPreset, setSelectedReturnPreset] = useState('medium');
    const [showWhatIf, setShowWhatIf] = useState(true); // Show by default
    const [showInputsRevertModal, setShowInputsRevertModal] = useState(false);
    const [whatIfInputs, setWhatIfInputs] = useState({
        liveToAge: '',
        moreIncome: '',
        annualFees: '', // Can be positive or negative
        sequenceRisk: ''
    });
    
    // Refs
    const resultsRef = useRef(null);
    const lastRunInputsRef = useRef(null);
    const lastRunMSIRef = useRef(0);
    const autoTypicalPresetTrackedRef = useRef(false);

    const getInputsForAdvancedTransfer = () => lastRunInputsRef.current ?? inputs;

    useEffect(() => {
        if (initialMobileState.scenarioPreset !== 'typical' || autoTypicalPresetTrackedRef.current) return;
        autoTypicalPresetTrackedRef.current = true;
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', 'preset_selected', {
                event_category: 'Calculator Usage',
                event_label: 'basic_mob_scenario_typical_auto',
                platform: 'mobile',
                calculator_tier: 'basic',
            });
        }
    }, []);

    const RETURN_PRESETS_MOB = {
        low:    { assumedReturn: 4.5 },
        medium: { assumedReturn: 6.5 },
        high:   { assumedReturn: 8.5 },
    };

    const applyScenarioPreset = (scenario) => {
        const updates = SCENARIO_PRESETS_MOB[scenario];
        if (!updates) return;
        setSelectedScenarioPreset(scenario);
        setInputs((prev) => ({ ...prev, ...updates }));
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', 'preset_selected', { event_category: 'Calculator Usage', event_label: `basic_mob_scenario_${scenario}`, platform: 'mobile', calculator_tier: 'basic' });
        }
    };

    const applyReturnPreset = (level) => {
        const updates = RETURN_PRESETS_MOB[level];
        if (!updates) return;
        setSelectedReturnPreset(level);
        setInputs((prev) => ({ ...prev, ...updates }));
        if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', 'preset_selected', { event_category: 'Calculator Usage', event_label: `basic_mob_return_${level}`, platform: 'mobile', calculator_tier: 'basic' });
        }
    };

    // Track Basic → retirement-calculator CTA exposure once results are ready.
    useEffect(() => {
        if (!hasCalculated || !results) return;
        trackBasicAdvancedCtaShown({ surface: 'results_primary', platform: 'mobile' });
    }, [hasCalculated, results]);
    
    // Save inputs to localStorage whenever they change (Pro only: save/restore is gated)
    React.useEffect(() => {
        if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) return;
        try {
            localStorage.setItem('basicCalculatorMobile_inputs', JSON.stringify(inputs));
        } catch (error) {
            console.error('Error saving inputs:', error);
        }
    }, [inputs]);
    
    // Hide header on scroll to maximize space
    React.useEffect(() => {
        let lastScroll = 0;
        const handleScroll = () => {
            const currentScroll = window.pageYOffset;
            setIsScrolled(currentScroll > 50);
            lastScroll = currentScroll;
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);
    
    const sections = [
        {
            id: 0,
            title: 'Personal Details',
            icon: '👤',
            subtitle: `${inputs.relationshipStatus === 'couple' ? 'Couple' : 'Single'}, Age ${inputs.yourAge}`,
            fields: [
                {
                    key: 'relationshipStatus',
                    label: 'Relationship Status',
                    type: 'select',
                    options: [
                        { value: 'single', label: 'Single' },
                        { value: 'couple', label: 'Couple' }
                    ]
                },
                {
                    key: 'yourAge',
                    label: 'Your Age',
                    type: 'number',
                    min: 18,
                    max: 100,
                    helper: 'Current age (18-100)'
                },
                {
                    key: 'yourGender',
                    label: 'Your Gender',
                    type: 'select',
                    options: [
                        { value: 'male', label: 'Male' },
                        { value: 'female', label: 'Female' }
                    ],
                    helper: 'For life expectancy calculations'
                },
                {
                    key: 'partnerAge',
                    label: 'Partner Age',
                    type: 'number',
                    min: 18,
                    max: 100,
                    helper: 'Enter partner\'s current age',
                    showIf: () => inputs.relationshipStatus === 'couple'
                },
                {
                    key: 'partnerGender',
                    label: 'Partner Gender',
                    type: 'select',
                    options: [
                        { value: 'male', label: 'Male' },
                        { value: 'female', label: 'Female' }
                    ],
                    helper: 'For life expectancy calculations',
                    showIf: () => inputs.relationshipStatus === 'couple'
                }
            ]
        },
        {
            id: 1,
            title: 'Super Balance',
            icon: '💰',
            subtitle: `$${inputs.yourSuper.toLocaleString()}${inputs.relationshipStatus === 'couple' ? ` + $${inputs.partnerSuper.toLocaleString()}` : ''}`,
            fields: [
                {
                    key: 'yourSuper',
                    label: 'Your Super Balance',
                    type: 'currency',
                    min: 0,
                    helper: 'Current superannuation balance'
                },
                {
                    key: 'partnerSuper',
                    label: 'Partner Super Balance',
                    type: 'currency',
                    min: 0,
                    showIf: () => inputs.relationshipStatus === 'couple'
                }
            ]
        },
        {
            id: 2,
            title: 'Retirement Plan',
            icon: '🎯',
            subtitle: `${inputs.retirementPeriod} years, $${inputs.desiredAnnualIncome.toLocaleString()}/yr`,
            fields: [
                {
                    key: 'retirementPeriod',
                    label: 'Retirement Period (Years)',
                    type: 'number',
                    min: 1,
                    max: 50,
                    helper: 'How many years in retirement?'
                },
                {
                    key: 'desiredAnnualIncome',
                    label: 'Target Annual Income (optional) — How much do you want to withdraw per year? ($, today\'s dollars)',
                    type: 'currency',
                    min: 0,
                    helper: "Target income per year (today's dollars)"
                }
            ]
        },
        {
            id: 3,
            title: 'Other Income & Assets',
            icon: '💵',
            subtitle: `Taxable: $${inputs.otherTaxableIncome.toLocaleString()}, Assets: $${inputs.assessableAssets.toLocaleString()}`,
            fields: [
                {
                    key: 'otherTaxableIncome',
                    label: 'Other Taxable Income ($/yr)',
                    type: 'currency',
                    min: 0,
                    helper: 'Gross income before tax (part-time work, rentals, etc.)'
                },
                {
                    key: 'otherNonTaxableIncome',
                    label: 'Other Non-Taxable Income ($/yr)',
                    type: 'currency',
                    min: 0,
                    helper: 'Tax-free income (if any)'
                },
                {
                    key: 'assessableAssets',
                    label: 'Assessable Assets (excluding super)',
                    type: 'currency',
                    min: 0,
                    helper: 'Investments, savings (not including home or super)'
                },
                {
                    key: 'isHomeowner',
                    label: 'Do you own your home?',
                    type: 'checkbox',
                    helper: 'Homeowners have higher Age Pension asset test threshold'
                },
                {
                    key: 'includePension',
                    label: 'Include Age Pension calculations',
                    type: 'checkbox',
                    helper: 'Calculate Age Pension eligibility and income'
                },
                {
                    key: 'pensionGrowthRate',
                    label: 'Pension Indexation Rate (%/yr)',
                    type: 'percentage',
                    min: 0,
                    max: 10,
                    step: 0.1,
                    helper: 'Indexes future pension rates and means-test thresholds together (default 0.3%)',
                    showIf: () => inputs.includePension
                }
            ]
        },
        {
            id: 4,
            title: 'Assumptions',
            icon: '📊',
            subtitle: `${inputs.assumedReturn}% return, ${inputs.assumedInflation}% inflation`,
            fields: [
                {
                    key: 'assumedReturn',
                    label: 'Expected Return (%/year)',
                    type: 'percentage',
                    min: 0,
                    max: 20,
                    step: 0.5,
                    helper: 'Typical: 5-8% for balanced portfolio',
                    presets: [
                        { label: 'Low (4.5%)', value: 4.5 },
                        { label: 'Medium (6.5%)', value: 6.5 },
                        { label: 'High (8.5%)', value: 8.5 }
                    ]
                },
                {
                    key: 'assumedInflation',
                    label: 'Expected Inflation (%/year)',
                    type: 'percentage',
                    min: 0,
                    max: 10,
                    step: 0.5,
                    helper: 'RBA target: 2-3%',
                    presets: [
                        { label: 'Low (2%)', value: 2 },
                        { label: 'Medium (2.5%)', value: 2.5 },
                        { label: 'High (3%)', value: 3 }
                    ]
                }
            ]
        },
        {
            id: 5,
            title: 'Fees',
            icon: '💳',
            subtitle: `$${inputs.adminFees + inputs.insuranceFees}/yr + ${(inputs.adminFeePercentage + inputs.investmentFees).toFixed(2)}%`,
            fields: [
                {
                    key: 'adminFees',
                    label: 'Fixed Admin Fee ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'E.g., AustralianSuper: $52/year'
                },
                {
                    key: 'adminFeePercentage',
                    label: 'Admin Fee (%)',
                    type: 'percentage',
                    min: 0,
                    max: 5,
                    step: 0.01,
                    helper: 'E.g., 0.10% of balance'
                },
                {
                    key: 'investmentFees',
                    label: 'Investment Fee (%)',
                    type: 'percentage',
                    min: 0,
                    max: 5,
                    step: 0.01,
                    helper: 'E.g., 0.50% of balance'
                },
                {
                    key: 'insuranceFees',
                    label: 'Insurance Premium ($/year)',
                    type: 'currency',
                    min: 0,
                    helper: 'Annual insurance cost'
                }
            ]
        }
    ];
    
    const handleInputChange = (key, value) => {
        trackFirstCalculatorInput({
            calculatorTier: 'basic',
            platform: 'mobile',
            interaction: 'edit'
        });
        // If changing to single, clear partner fields
        if (key === 'relationshipStatus' && value === 'single') {
            setInputs(prev => ({
                ...prev,
                [key]: value,
                partnerAge: '',
                partnerSuper: ''
            }));
        } else {
            setInputs(prev => ({ ...prev, [key]: value }));
        }
        // Reset results when inputs change
        if (hasCalculated) {
            setHasCalculated(false);
            setResults(null);
        }
    };
    
    const toggleSection = (index) => {
        const newExpanded = expandedSection === index ? -1 : index;
        setExpandedSection(newExpanded);
        
        // Scroll to the section that was just opened
        if (newExpanded !== -1) {
            setTimeout(() => {
                const sectionElement = document.getElementById(`basic-section-${index}`);
                if (sectionElement) {
                    const headerOffset = 60; // Account for sticky header
                    const elementPosition = sectionElement.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                    
                    window.scrollTo({
                        top: offsetPosition,
                        behavior: 'smooth'
                    });
                }
            }, 100);
        }
    };
    
    // Tax calculation function (copied from App.js for accuracy)
    const calculateTax = (income, { isCouple = false, hasPrivateHealthInsurance = true } = {}) => {
        let tax = 0;
        const brackets = FinancialConfig.INCOME_TAX_BRACKETS;
        for (const bracket of brackets) {
            if (income > bracket.min) {
                const taxableInBracket = Math.min(income, bracket.max) - bracket.min;
                tax += taxableInBracket * bracket.rate;
            }
        }
        
        let lito = 0;
        if (income <= 37500) lito = 700;
        else if (income <= 45000) lito = 700 - (income - 37500) * 0.05;
        else if (income <= 66667) lito = 325 - (income - 45000) * 0.015;
        tax = Math.max(0, tax - lito);
        
        const baseMedicareLevy = income * 0.02;
        let surcharge = 0;
        if (!hasPrivateHealthInsurance) {
            const singleThresholds = { tier1: 97000, tier2: 113000, tier3: 151000 };
            const familyThresholds = { tier1: 194000, tier2: 226000, tier3: 302000 };
            const thresholds = isCouple ? familyThresholds : singleThresholds;
            if (income > thresholds.tier3) surcharge = income * 0.015;
            else if (income > thresholds.tier2) surcharge = income * 0.0125;
            else if (income > thresholds.tier1) surcharge = income * 0.01;
        }
        return tax + baseMedicareLevy + surcharge;
    };

    const getAfterTaxIncome = (taxableIncome, isCouple) => {
        if (!taxableIncome || taxableIncome <= 0) return 0;
        const tax = calculateTax(taxableIncome, { isCouple, hasPrivateHealthInsurance: true });
        const afterTax = Math.max(0, taxableIncome - tax);
        return afterTax;
    };

    const getBasicWhatIfContext = () => {
        const snap = lastRunInputsRef.current || inputs;
        return buildBasicCalcContext(snap, getAfterTaxIncome);
    };

    const getBasicBaselineMSI = () => {
        const fromRun = lastRunMSIRef.current;
        if (fromRun > 0) return fromRun;
        return Math.round(computeBasicMSI(getBasicWhatIfContext()));
    };

    const buildMobileWhatIfResults = () => {
        if (!lastRunInputsRef.current || !results) return {};
        const ctx = getBasicWhatIfContext();
        const baseline = getBasicBaselineMSI();
        const out = {};
        if (whatIfInputs.liveToAge !== '') {
            try {
                out.liveToAge = runBasicLiveToAgeWhatIf(ctx, parseInt(whatIfInputs.liveToAge, 10) || 100, baseline);
            } catch (_) { /* invalid target age */ }
        }
        if (whatIfInputs.moreIncome !== '') {
            out.moreIncome = runBasicMoreIncomeWhatIf(ctx, parseInt(whatIfInputs.moreIncome, 10) || 0, baseline);
        }
        if (whatIfInputs.annualFees !== '') {
            out.annualFees = runBasicAnnualFeesWhatIf(ctx, parseFloat(whatIfInputs.annualFees) || 0, baseline);
            out.fees = out.annualFees;
        }
        if (whatIfInputs.sequenceRisk !== '') {
            out.sequenceRisk = runBasicSequenceRiskWhatIf(ctx, parseInt(whatIfInputs.sequenceRisk, 10) || 0, baseline);
        }
        return out;
    };

    const handleCalculate = async () => {
        try {
            // Fire funnel event every time (no ref check, debug)
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'first_calculator_input', { event_category: 'funnel', event_label: 'mobile_calc', value: 1, calculator_tier: 'basic' });
            }
            setIsCalculating(true);
            await new Promise(resolve => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setTimeout(resolve, 100);
                    });
                });
            });
            
            // Parse all inputs - EXACT same as desktop
            const isCouple = inputs.relationshipStatus === 'couple';
            const totalSuper = parseFloat(inputs.yourSuper || 0) + (isCouple ? parseFloat(inputs.partnerSuper || 0) : 0);
            const retirementPeriod = resolveBasicRetirementPeriodYears(inputs.retirementPeriod);
            const desiredIncome = parseFloat(inputs.desiredAnnualIncome || 0);
            const annualReturn = parseFloat(inputs.assumedReturn || 6.5) / 100;
            const inflation = parseFloat(inputs.assumedInflation || 2.5) / 100;
            const netReturn = annualReturn - inflation;
            
            const rawAdminPct = parseFloat(inputs.adminFeePercentage);
            const resolvedAdminFeePct = Number.isFinite(rawAdminPct) ? rawAdminPct : 0.10;
            const calcInputs = { ...inputs, adminFeePercentage: resolvedAdminFeePct };
            const calcCtx = buildBasicCalcContext(calcInputs, getAfterTaxIncome);

            // Same MSI path as desktop Run (WithFees + preservation-age projection)
            const maxSustainableIncome = computeBasicMSI(calcCtx);
            const adminFees = calcCtx.adminFees;
            const investmentFees = calcCtx.investmentFees;
            const insuranceFees = calcCtx.insuranceFees;
            const adminFeePercentage = calcCtx.adminFeePercentage;
            const otherIncome = calcCtx.otherIncome;
            const assessableAssets = calcCtx.assessableAssets;
            const isHomeowner = calcCtx.isHomeowner;
            const includePension = calcCtx.includePension;
            const pensionGrowthRate = calcCtx.pensionGrowthRate;
            const yourAge = calcCtx.yourAge;
            const afterTaxIncome = getAfterTaxIncome(parseFloat(inputs.otherTaxableIncome || 0), isCouple);
            
            // Generate chart data using SimulationEngine (EXACT SAME AS DESKTOP)
            const chartData = [];
            let balanceMSI = totalSuper;
            let balanceDesired = totalSuper;
            
            const params = {
                netReturn,
                otherIncome,
                assessableAssets,
                isCouple,
                isHomeowner,
                includePension,
                adminFees,
                investmentFees,
                insuranceFees,
                adminFeePercentage,
                pensionGrowthRate,
                startingAge: yourAge,
                partnerStartingAge: calcCtx.isCouple ? calcCtx.partnerAge : null,
                memberShares: calcCtx.memberShares
            };
            
            for (let year = 0; year < retirementPeriod; year++) {
                const startingBalanceMSI = balanceMSI;
                const startingBalanceDesired = balanceDesired;
                
                // MSI trajectory - Use SimulationEngine._simulateOneYear
                const msiResult = SimulationEngine._simulateOneYear(balanceMSI, maxSustainableIncome, year, params);
                balanceMSI = msiResult.finalBalance;
                
                // Desired income trajectory
                const desiredResult = SimulationEngine._simulateOneYear(balanceDesired, desiredIncome, year, params);
                balanceDesired = desiredResult.finalBalance;
                
                chartData.push({
                    year: year + 1,
                    age: yourAge + year,
                    balance: Math.round(startingBalanceMSI),
                    startingBalance: Math.round(startingBalanceMSI),
                    endingBalance: Math.round(balanceMSI),
                    balanceWithDesiredIncome: Math.round(balanceDesired),
                    earnings: Math.round(msiResult.earnings || 0),
                    fees: Math.round(msiResult.fees || 0),
                    withdrawal: Math.round(msiResult.withdrawal || 0),
                    withdrawalFromSuper: Math.round(msiResult.withdrawal || 0),
                    pension: Math.round(msiResult.pension || 0),
                    totalIncome: computeBasicAchievableYearIncome(msiResult, otherIncome),
                    maxSustainableIncome: Math.round(maxSustainableIncome),
                    // Income breakdown for stacked bar chart
                    otherTaxableIncome: Math.round(afterTaxIncome),
                    otherNonTaxableIncome: Math.round(parseFloat(inputs.otherNonTaxableIncome || 0)),
                    // Desired income trajectory
                    withdrawalFromSuperDesired: Math.round(desiredResult.withdrawal || 0),
                    pensionDesired: Math.round(desiredResult.pension || 0)
                });
            }
            
            const finalBalance = chartData[chartData.length - 1]?.endingBalance || 0;
            
            setResults({
                finalBalance: Math.round(finalBalance),
                maxSustainableIncome: Math.round(maxSustainableIncome),
                desiredAnnualIncome: Number(inputs.desiredAnnualIncome) || 0,
                successRate: finalBalance > 0 ? 100 : Math.round((chartData.filter(d => d.endingBalance > 0).length / chartData.length) * 100),
                chartData: chartData,
                // Store for what-if scenarios
                params: {
                    totalSuper, retirementPeriod, netReturn, otherIncome, assessableAssets,
                    isCouple, isHomeowner, includePension, adminFees, investmentFees,
                    insuranceFees, adminFeePercentage, pensionGrowthRate, yourAge
                }
            });
            
            setHasCalculated(true);
            lastRunMSIRef.current = Math.round(maxSustainableIncome);
            lastRunInputsRef.current = { ...inputs };
            // Free users: keep inputs on-screen for this visit (no localStorage restore). Show soft note instead of wiping.
            if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO)) {
                setShowInputsRevertModal(true);
            }
            trackCalculationComplete({
                calculatorTier: 'basic',
                calculatorType: 'basic_retirement',
                platform: 'mobile'
            });
            setIsCalculating(false);
            // Scroll results into view after render — single call, enough delay for DOM update
            setTimeout(() => {
                resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
            
        } catch (error) {
            console.error('Calculation error:', error);
            setIsCalculating(false);
            alert(`Error: ${error.message}. Please check all inputs are valid numbers.`);
        }
    };
    
    const renderField = (field) => {
        if (field.showIf && !field.showIf()) return null;
        
        const value = inputs[field.key];
        
        return (
            <div key={field.key} className="mb-5">
                <label className="block text-base font-bold text-gray-900 mb-2">
                    {field.label}
                </label>
                
                {field.type === 'select' && (
                    <select
                        value={value}
                        onChange={(e) => handleInputChange(field.key, e.target.value)}
                        className="w-full px-4 py-4 text-base font-medium border-2 border-gray-300 rounded-xl bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-200 appearance-none"
                        style={{ 
                            fontSize: '16px',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 12px center',
                            backgroundSize: '24px'
                        }}
                    >
                        {field.options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                )}
                
                {field.type === 'checkbox' && (
                    <label className="flex items-center gap-4 p-5 bg-gray-50 rounded-xl border-2 border-gray-200 active:bg-gray-100">
                        <input
                            type="checkbox"
                            checked={value}
                            onChange={(e) => handleInputChange(field.key, e.target.checked)}
                            className="w-7 h-7 text-blue-600 rounded-md flex-shrink-0"
                        />
                        <span className="text-base font-medium text-gray-800 flex-1">
                            {field.label}
                        </span>
                    </label>
                )}
                
                {(field.type === 'number' || field.type === 'currency' || field.type === 'percentage') && (
                    <>
                        <div className="relative">
                            <input
                                type="number"
                                value={value === '' ? '' : value}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    // Allow empty string or valid number
                                    if (val === '') {
                                        handleInputChange(field.key, '');
                                    } else {
                                        handleInputChange(field.key, parseFloat(val) || 0);
                                    }
                                }}
                                placeholder={field.type === 'currency' ? '0' : field.type === 'percentage' ? '0.0' : '0'}
                                min={field.min}
                                max={field.max}
                                step={field.step || 1}
                                className="w-full px-5 py-5 text-xl font-bold text-right border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-200"
                                style={{ fontSize: '18px' }}
                            />
                            {field.type === 'currency' && (
                                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-500">$</span>
                            )}
                            {field.type === 'percentage' && (
                                <span className="absolute right-5 top-1/2 -translate-y-1/2 text-xl font-bold text-gray-500">%</span>
                            )}
                        </div>
                        
                        {/* Presets for assumptions */}
                        {field.presets && (
                            <div className="mt-3 grid grid-cols-3 gap-2">
                                {field.presets.map(preset => (
                                    <button
                                        key={preset.value}
                                        onClick={() => handleInputChange(field.key, preset.value)}
                                        className={`py-3 px-2 text-sm font-semibold rounded-lg border-2 transition ${
                                            value === preset.value
                                                ? 'bg-blue-600 text-white border-blue-600'
                                                : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                                        }`}
                                    >
                                        {preset.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </>
                )}
                
                {field.helper && (
                    <p className="mt-2 text-sm text-gray-600 leading-relaxed">{field.helper}</p>
                )}
            </div>
        );
    };
    
    return (
        <div className="min-h-screen bg-gray-50" style={{ paddingBottom: 'max(calc(env(safe-area-inset-bottom) + 10.5rem), 11rem)' }}>
            {/* Loading Spinner - Calculations */}
            {isCalculating && (
                <MobileLoadingSpinner 
                    message="Calculating retirement plan..."
                    subMessage="Running simulation..."
                />
            )}
            
            {/* Loading Spinner - PDF Export */}
            {isExporting && (
                <MobileLoadingSpinner 
                    message="Generating PDF..."
                    subMessage="Please wait while we create your report..."
                />
            )}
            
            {/* Minimal Header - Hides when scrolling */}
            <div className={`bg-blue-600 text-white px-3 sticky top-0 z-20 shadow-md transition-all ${
                isScrolled ? 'py-2' : 'py-3'
            }`}
                 style={{ paddingTop: isScrolled ? '8px' : 'max(env(safe-area-inset-top), 12px)' }}>
                <div className="flex items-center justify-between">
                    <button
                        onClick={onBack}
                        className="p-1 active:bg-blue-700 rounded"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <h1 className={`font-bold transition-all ${isScrolled ? 'text-sm' : 'text-base'}`}>
                        Basic Retirement
                    </h1>
                    <div className="w-7"></div>
                </div>
            </div>
            
            {!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) && (
                <p className="px-3 py-1.5 text-xs text-gray-500 text-center">Free: edit and re-run anytime this visit. Subscribe to keep inputs after you leave.</p>
            )}

            <div className="px-3 pt-2">
                <p className="text-xs font-semibold text-gray-600 mb-1">Example profile <span className="font-normal text-gray-400">(not your real numbers)</span></p>
                <div className="flex flex-col gap-2 mb-3">
                    {[
                        { key: 'early',   label: 'Early retiree',   sublabel: 'Age 60 · $400k · $40k/yr' },
                        { key: 'typical', label: 'Typical retiree', sublabel: 'Age 65 · $600k · $55k/yr' },
                        { key: 'high',    label: 'High balance',    sublabel: 'Age 67 · $900k · $70k/yr' },
                    ].map((s) => (
                        <button key={s.key} type="button" onClick={() => applyScenarioPreset(s.key)}
                            className={`px-3 py-2 rounded-lg text-sm border transition text-left ${selectedScenarioPreset === s.key ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                            <span className="font-medium block">{s.label}</span>
                            <span className={`text-xs ${selectedScenarioPreset === s.key ? 'text-blue-100' : 'text-gray-500'}`}>{s.sublabel}</span>
                        </button>
                    ))}
                </div>
                <p className="text-xs font-semibold text-gray-600 mb-1">Return assumption</p>
                <div className="flex gap-2 mb-2">
                    {[
                        { key: 'low',    label: 'Low',    sublabel: '4.5%' },
                        { key: 'medium', label: 'Medium', sublabel: '6.5%' },
                        { key: 'high',   label: 'High',   sublabel: '8.5%' },
                    ].map((r) => (
                        <button key={r.key} type="button" onClick={() => applyReturnPreset(r.key)}
                            className={`flex-1 px-2 py-1.5 rounded-lg text-sm border transition text-center ${selectedReturnPreset === r.key ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-700 border-gray-300'}`}>
                            <span className="font-medium block">{r.label}</span>
                            <span className={`text-xs ${selectedReturnPreset === r.key ? 'text-teal-100' : 'text-gray-500'}`}>{r.sublabel}</span>
                        </button>
                    ))}
                </div>
                <p className="text-xs text-gray-500 mb-1">Pick what fits — edit any field before running.</p>
                <p className="text-xs text-emerald-700 font-medium">
                    {selectedScenarioPreset === 'typical'
                        ? 'Typical retiree loaded — switch example or edit your details below.'
                        : 'Pick an example above or fill in your own details.'}
                </p>
            </div>
            
            {/* Accordion Sections */}
            <div className="p-3 space-y-2">
                {sections.map((section, index) => {
                    const isExpanded = expandedSection === index;
                    const isCompleted = index < expandedSection;
                    
                    return (
                        <div 
                            key={section.id}
                            id={`basic-section-${index}`}
                            className={`bg-white rounded-2xl shadow-md overflow-hidden transition-all ${
                                isExpanded ? 'ring-4 ring-blue-400' : ''
                            }`}
                        >
                            {/* Section Header */}
                            <button
                                onClick={() => toggleSection(index)}
                                className="w-full p-5 flex items-center justify-between active:bg-gray-50 transition"
                            >
                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                    <div className="text-3xl flex-shrink-0">{section.icon}</div>
                                    <div className="text-left flex-1 min-w-0">
                                        <div className="font-bold text-gray-900 text-base mb-1">
                                            {section.title}
                                        </div>
                                        <div className="text-sm text-gray-600 truncate">
                                            {section.subtitle}
                                        </div>
                                    </div>
                                    {isCompleted && (
                                        <div className="bg-green-500 text-white rounded-full p-1 flex-shrink-0">
                                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                                
                                <svg 
                                    className={`w-6 h-6 text-gray-400 transition-transform flex-shrink-0 ml-2 ${
                                        isExpanded ? 'rotate-180' : ''
                                    }`}
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            
                        {/* Section Content */}
                        {isExpanded && (
                            <div className="px-4 pb-4 bg-gray-50">
                                {/* Couple Mode Explanation */}
                                {section.id === 0 && inputs.relationshipStatus === 'couple' && (
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm">
                                        <p className="font-semibold text-blue-900 mb-2">📋 How Couple Mode Works:</p>
                                        <ul className="text-xs text-blue-800 space-y-1">
                                            <li>✓ Super combined for income calculation</li>
                                            <li>✓ Pension assessed separately per person</li>
                                            <li>✓ Assumes both retire at same time</li>
                                        </ul>
                                    </div>
                                )}
                                <div className="border-t border-gray-200 pt-4">
                                    {section.fields.map(field => renderField(field))}
                                        
                                        {/* Next Button */}
                                        {index < sections.length - 1 && (
                                            <button
                                                onClick={() => {
                                                    toggleSection(index + 1);
                                                }}
                                                className="w-full mt-6 py-4 bg-blue-600 text-white font-bold text-base rounded-xl shadow-lg hover:bg-blue-700 active:scale-98 transition"
                                            >
                                                Next: {sections[index + 1].title} →
                                            </button>
                                        )}
                                        {index === sections.length - 1 && (
                                            <p className="mt-4 mb-2 text-sm text-gray-500 text-center">
                                                Ready when you are — use Run below.
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {/* Clear sticky Run bar so last Fees fields are not covered */}
            <div className="h-4" aria-hidden="true" />
            
            {/* Results Section */}
            {hasCalculated && results && (
                <div ref={resultsRef} className="p-4 space-y-4 mb-20">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-1 rounded-2xl shadow-xl">
                        <div className="bg-white rounded-xl p-6">
                            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
                                Your Results
                            </h2>
                            
                            {(() => {
                                const runInputs = getInputsForAdvancedTransfer();
                                return (
                            <>
                            {runInputs.relationshipStatus === 'couple' && Math.abs((runInputs.yourAge || 0) - (runInputs.partnerAge || 0)) > 2 && (
                              <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 mb-4">
                                <p className="text-xs text-amber-800 font-semibold">⚠️ Note:</p>
                                <p className="text-xs text-amber-700 mt-1">This projection assumes both partners retire at the same time. If you plan to retire at different ages, the result will differ.</p>
                              </div>
                            )}
                            
                            {/* Final Balance */}
                            <div className="bg-blue-50 p-6 rounded-xl border-2 border-blue-200 mb-4">
                                <div className="text-sm font-semibold text-gray-600 mb-2">
                                    Final Super Balance
                                </div>
                                <div className="text-4xl font-bold text-blue-600 mb-1">
                                    ${results.finalBalance.toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500">
                                    After {runInputs.retirementPeriod} years
                                </div>
                            </div>
                            
                            {/* Max Sustainable Income */}
                            <div className="bg-green-50 p-6 rounded-xl border-2 border-green-200 mb-4">
                                <div className="text-sm font-semibold text-gray-600 mb-2">
                                    Maximum Annual Income
                                </div>
                                <div className="text-4xl font-bold text-green-600 mb-1">
                                    ${results.maxSustainableIncome.toLocaleString()}
                                </div>
                                <div className="text-xs text-gray-500">
                                    Per year for {runInputs.retirementPeriod} years
                                </div>
                                <p className="text-xs text-green-800 mt-2 opacity-90">Same amount each year your super can sustain for your full retirement. Includes Age Pension and fees; in today's dollars.</p>
                            </div>
                            </>
                                );
                            })()}
                            
                            {/* Plan Success - Check income goal vs max sustainable */}
                            {(() => {
                                const desiredIncome = results.desiredAnnualIncome || 0;
                                const maxIncome = results.maxSustainableIncome;
                                const shortfall = desiredIncome - maxIncome;
                                const canAchieve = desiredIncome === 0 || maxIncome >= desiredIncome;
                                
                                if (desiredIncome > 0 && !canAchieve) {
                                    return (
                                        <div className="bg-red-50 p-6 rounded-xl border-2 border-red-300">
                                            <div className="text-sm font-semibold text-red-800 mb-2">
                                                ⚠️ Income Shortfall
                                            </div>
                                            <div className="text-lg font-bold text-red-700 mb-2">
                                                You can withdraw ${Math.round(maxIncome).toLocaleString()}/year
                                            </div>
                                            <div className="text-sm text-red-800 mb-2">
                                                But you wanted ${Math.round(desiredIncome).toLocaleString()}/year
                                            </div>
                                            <div className="text-2xl font-bold text-red-600 mb-2">
                                                Shortfall: ${Math.round(shortfall).toLocaleString()}/year
                                            </div>
                                            <p className="text-xs text-red-600 mt-2">
                                                You'd need to increase super, work longer, or reduce your desired income.
                                            </p>
                                        </div>
                                    );
                                }
                                
                                return (
                                    <div className="bg-purple-50 p-6 rounded-xl border-2 border-purple-200">
                                        <div className="text-sm font-semibold text-gray-600 mb-2">
                                            Plan Success
                                        </div>
                                        <div className="text-4xl font-bold text-purple-600 mb-1">
                                            ✓
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {desiredIncome > 0 
                                                ? 'You can achieve your desired income' 
                                                : 'Your money should last'}
                                        </div>
                                        <p className="caveat-basic-note text-sm mt-2 font-medium leading-snug">
                                            Note: Based purely on your inputs — return and inflation are constant and don&apos;t reflect real-life market variation. The Advanced calculator lets you test your plan against real-world results.
                                        </p>
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    <div className="rounded-xl border-2 border-purple-400 bg-purple-50 p-4">
                        <p className="text-sm font-bold text-purple-900 mb-2">Next step: continue in the retirement calculator</p>
                        <p className="text-xs text-purple-800 mb-3">Your numbers transfer. Stress-test the same plan against real market history — free runs available.</p>
                        <button
                            type="button"
                            onClick={() => {
                                CalculatorBridge.navigateBasicToAdvanced(getInputsForAdvancedTransfer(), { eventLabel: 'Basic Mobile results primary CTA' });
                                if (onGoToAdvanced) onGoToAdvanced();
                            }}
                            className="w-full py-3 text-sm font-bold rounded-lg bg-purple-600 text-white"
                        >
                            Continue in the retirement calculator →
                        </button>
                        {!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) && (
                            <p className="text-xs text-purple-800 mt-3 mb-0">
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (typeof window.gtag === 'function') {
                                            window.gtag('event', 'basic_results_pricing_click', { event_category: 'conversion', event_label: 'Basic Mobile results primary', value: 1, calculator_tier: 'basic' });
                                        }
                                        openPricingModal({ source: 'basic_results_primary', calculatorTier: 'basic' });
                                    }}
                                    className="underline font-semibold text-purple-900"
                                >
                                    Save inputs / unlimited runs later — see pricing
                                </button>
                                <span> · Basic PDF summary is free by email below.</span>
                            </p>
                        )}
                        {SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.SAVE_RESTORE_SCENARIO) && (
                            <p className="text-xs text-purple-700 mt-2 mb-0">Basic PDF summary is free by email below.</p>
                        )}
                    </div>

                    {showInputsRevertModal && (
                        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4" role="status">
                            <div className="flex gap-3 items-start">
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-blue-900 mb-1">Your numbers are still in the form above</p>
                                    <p className="text-xs text-blue-800 mb-0">
                                        Edit any field and run again on this visit. They won&apos;t be remembered after you leave — subscribe to auto-save.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowInputsRevertModal(false)}
                                    className="text-blue-700 text-lg leading-none px-1 flex-shrink-0"
                                    aria-label="Dismiss"
                                >
                                    ×
                                </button>
                            </div>
                            <div className="flex gap-2 mt-3">
                                <button
                                    type="button"
                                    onClick={() => setShowInputsRevertModal(false)}
                                    className="flex-1 py-2.5 px-3 bg-white text-blue-900 font-semibold rounded-lg text-sm border border-blue-200 min-h-[44px]"
                                >
                                    Got it
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowInputsRevertModal(false);
                                        openPricingModal({
                                            source: 'basic_mobile_soft_wipe',
                                            calculatorTier: 'basic',
                                            surface: 'basic_mobile_persistence_note',
                                        });
                                    }}
                                    className="flex-1 py-2.5 px-3 bg-blue-600 text-white font-semibold rounded-lg text-sm min-h-[44px]"
                                >
                                    See pricing
                                </button>
                            </div>
                        </div>
                    )}
                    
                    {/* Insight copy only — primary handoff is the block above */}
                    {(() => {
                        const msi = results.maxSustainableIncome || 0;
                        const desired = results.desiredAnnualIncome || 0;
                        const shortfall = desired > 0 && msi > 0 && desired > msi;
                        const borderColor = shortfall ? 'border-amber-400' : 'border-blue-300';
                        const bgColor = shortfall ? 'bg-amber-50' : 'bg-blue-50';
                        return (
                            <div className={`rounded-xl border-2 ${borderColor} ${bgColor} p-4`}>
                                <p className="text-sm font-bold text-gray-900 mb-1">
                                    {shortfall
                                        ? `⚠ Gap — you want $${Math.round(desired/1000)}k/yr but can sustain $${Math.round(msi/1000)}k/yr`
                                        : `✓ This is a constant-return estimate — real markets vary`
                                    }
                                </p>
                                <p className="text-xs text-gray-600 mb-0">
                                    {shortfall
                                        ? 'Use the continue button above to stress-test with 98 years of real historical data and see if this gap holds across different market conditions.'
                                        : 'Use the continue button above to test your plan against actual historical sequences — including crashes, recoveries, and sequence-of-returns risk.'
                                    }
                                </p>
                            </div>
                        );
                    })()}

                    {/* Email Capture - After insight (mobile Basic) */}
                    <div className="mt-4" aria-label="Email your results">
                        <EmailCaptureForm
                            calculatorType="basic"
                            results={{
                                ...buildBasicEmailResults(results.chartData || [], {
                                    maxSustainableIncome: results.maxSustainableIncome,
                                    successRate: results.successRate,
                                    periodLength: getInputsForAdvancedTransfer().retirementPeriod
                                }),
                                chartData: results.chartData
                            }}
                            inputs={{
                                ...getInputsForAdvancedTransfer(),
                                periodLength: getInputsForAdvancedTransfer().retirementPeriod
                            }}
                            getPdfBase64={() => {
                                const whatIfResults = buildMobileWhatIfResults();
                                return generateBasicCalculatorPdfBase64(
                                    getInputsForAdvancedTransfer(),
                                    results.chartData || [],
                                    whatIfResults
                                );
                            }}
                        />
                    </div>

                    {/* View Detailed Chart Button - Immediately after results */}
                    <div className="mt-4">
                        <button 
                            onClick={() => setShowDetailedChart(true)}
                            className="w-full py-3 bg-white text-slate-700 font-semibold text-sm rounded-xl border-2 border-slate-200 active:bg-slate-50"
                        >
                            View detailed chart
                        </button>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="space-y-3">
                        <button 
                            onClick={async () => {
                                if (!SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.PDF_EXPORT)) {
                                    startRecommendedCheckout({
                                        tier: 'essential',
                                        billing: 'monthly',
                                        source: 'pdf_export',
                                        calculatorTier: 'basic',
                                        surface: 'basic_mobile_pdf_export',
                                    }).catch(alertCheckoutFailure);
                                    return;
                                }
                                try {
                                    setIsExporting(true);
                                    await new Promise(resolve => {
                                        requestAnimationFrame(() => {
                                            requestAnimationFrame(() => {
                                                setTimeout(resolve, 100);
                                            });
                                        });
                                    });
                                    
                                    const whatIfResults = buildMobileWhatIfResults();
                                    await PDFExportManager.exportBasicCalculator(
                                        getInputsForAdvancedTransfer(),
                                        results.chartData,
                                        whatIfResults,
                                        {}
                                    );
                                    
                                    setIsExporting(false);
                                } catch (error) {
                                    console.error('PDF export error:', error);
                                    setIsExporting(false);
                                    alert('Error generating PDF. Please try again.');
                                }
                            }}
                            className="w-full py-4 bg-gray-600 text-white font-bold text-base rounded-xl shadow-lg active:bg-gray-700"
                        >
                            📄 Export PDF
                        </button>
                        <button 
                            onClick={() => {
                                setHasCalculated(false);
                                setResults(null);
                                setShowInputsRevertModal(false);
                                setExpandedSection(0);
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="w-full py-4 bg-white text-gray-700 font-bold text-base rounded-xl border-2 border-gray-300"
                        >
                            🔄 New Calculation
                        </button>
                    </div>

                    <CalculationTransparency
                        kind="basic"
                        inputs={getInputsForAdvancedTransfer()}
                        results={results}
                        chartData={results.chartData || []}
                        className="mt-6"
                    />
                </div>
            )}
            
            {/* Detailed Chart Modal */}
            {showDetailedChart && results && (
                <div className="fixed inset-0 bg-black bg-opacity-70 z-50 overflow-auto" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
                    <div className="min-h-screen p-4">
                        <div className="bg-white rounded-xl w-full max-w-4xl mx-auto mb-4">
                            {/* Modal Header */}
                            <div className="bg-purple-600 text-white p-4 rounded-t-xl flex justify-between items-center sticky top-0 z-10">
                                <h3 className="text-lg font-bold">Detailed Analysis</h3>
                                <button 
                                    onClick={() => setShowDetailedChart(false)}
                                    className="text-white bg-purple-700 hover:bg-purple-800 p-2 rounded-lg w-10 h-10 flex items-center justify-center"
                                >
                                    ✕
                                </button>
                            </div>
                            
                            {/* Modal Content - Scrollable */}
                            <div className="p-4 space-y-6">
                                {/* Balance Chart */}
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-3">Super Balance Over Time</h3>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <AreaChart data={results.chartData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottom', offset: -5 }} />
                                            <YAxis tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                                            <Tooltip formatter={(val) => `$${val.toLocaleString()}`} />
                                            <Legend />
                                            {(results.desiredAnnualIncome || 0) > 0 && (
                                                <Area 
                                                    type="monotone" 
                                                    dataKey="balanceWithDesiredIncome" 
                                                    name="With Desired Income"
                                                    stroke="#EF4444" 
                                                    fill="#EF4444" 
                                                    fillOpacity={0.2}
                                                />
                                            )}
                                            <Area 
                                                type="monotone" 
                                                dataKey="endingBalance" 
                                                name="Max Sustainable"
                                                stroke="#3B82F6" 
                                                fill="#3B82F6" 
                                                fillOpacity={0.3}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                                
                                {/* Income Chart - MSI */}
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-3">Maximum Sustainable Income</h3>
                                    <ResponsiveContainer width="100%" height={250}>
                                        <BarChart data={results.chartData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottom', offset: -5 }} />
                                            <YAxis tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                                            <Tooltip formatter={(val) => `$${val.toLocaleString()}`} />
                                            <Legend />
                                            <Bar dataKey="otherTaxableIncome" stackId="a" fill="#10B981" name="Taxable Income" />
                                            <Bar dataKey="otherNonTaxableIncome" stackId="a" fill="#34D399" name="Non-Taxable" />
                                            <Bar dataKey="withdrawalFromSuper" stackId="a" fill="#3B82F6" name="Super Withdrawal" />
                                            <Bar dataKey="pension" stackId="a" fill="#F59E0B" name="Age Pension" />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                                
                                {/* Income Chart - Desired (if different) */}
                                {(results.desiredAnnualIncome || 0) > 0 && (results.desiredAnnualIncome || 0) !== results.maxSustainableIncome && (
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900 mb-3">Desired Income Breakdown</h3>
                                        <ResponsiveContainer width="100%" height={250}>
                                            <BarChart data={results.chartData}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="year" label={{ value: 'Year', position: 'insideBottom', offset: -5 }} />
                                                <YAxis tickFormatter={(val) => `$${(val/1000).toFixed(0)}k`} />
                                                <Tooltip formatter={(val) => `$${val.toLocaleString()}`} />
                                                <Legend />
                                                <Bar dataKey="otherTaxableIncome" stackId="b" fill="#10B981" name="Taxable Income" />
                                                <Bar dataKey="otherNonTaxableIncome" stackId="b" fill="#34D399" name="Non-Taxable" />
                                                <Bar dataKey="withdrawalFromSuperDesired" stackId="b" fill="#EF4444" name="Super Withdrawal" />
                                                <Bar dataKey="pensionDesired" stackId="b" fill="#F59E0B" name="Age Pension" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                )}
                                
                                {/* Data Table */}
                                <div>
                                    <h3 className="text-lg font-bold text-gray-900 mb-3">Year-by-Year Details</h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm border-collapse">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="p-2 text-left border">Year</th>
                                                    <th className="p-2 text-left border">Age</th>
                                                    <th className="p-2 text-right border">Balance</th>
                                                    <th className="p-2 text-right border">Income</th>
                                                    <th className="p-2 text-right border">Pension</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {results.chartData.map((row, idx) => (
                                                    <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                                        <td className="p-2 border">{row.year}</td>
                                                        <td className="p-2 border">{row.age}</td>
                                                        <td className="p-2 text-right border font-bold">${(row.endingBalance || 0).toLocaleString()}</td>
                                                        <td className="p-2 text-right border">${(row.totalIncome || 0).toLocaleString()}</td>
                                                        <td className="p-2 text-right border">${(row.pension || 0).toLocaleString()}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                                
                                {/* What-If Scenarios (Essential+); visible for all, inputs/buttons disabled for free */}
                                {results.params && (() => {
                                    const whatIfUnlocked = SubscriptionManager.hasAccess(SubscriptionManager.FEATURES.BASIC_WHATIFS);
                                    return (
                                    <div>
                                        <div className="mb-3">
                                            <h3 className="text-lg font-bold text-gray-900 mb-1">What-If Scenarios</h3>
                                            {!whatIfUnlocked && (
                                                <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between gap-2 flex-wrap">
                                                <span className="text-xs text-amber-800">Unlock with Advanced Retirement to run.</span>
                                                    <button type="button" onClick={() => openPricingModal({ source: 'basic_mobile_upgrade', calculatorTier: 'basic' })} className="px-2 py-1 text-xs font-semibold bg-amber-600 text-white rounded">Upgrade</button>
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => setShowWhatIf(!showWhatIf)}
                                                className="text-sm font-semibold text-blue-600 active:text-blue-700"
                                            >
                                                {showWhatIf ? '▲ Hide Controls' : '▼ Show Controls'}
                                            </button>
                                        </div>
                                        
                                        {/* What-If Input Controls */}
                                        {showWhatIf && (
                                            <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-3">
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Live to Age:
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={whatIfInputs.liveToAge}
                                                        onChange={(e) => {
                                                            if (!whatIfUnlocked) return;
                                                            const val = e.target.value;
                                                            setWhatIfInputs(prev => ({ ...prev, liveToAge: val === '' ? '' : parseInt(val) || '' }));
                                                        }}
                                                        placeholder="100"
                                                        disabled={!whatIfUnlocked}
                                                        className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg disabled:opacity-60 disabled:bg-gray-100"
                                                        style={{ fontSize: '16px' }}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Extra Income ($/year):
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={whatIfInputs.moreIncome}
                                                        onChange={(e) => {
                                                            if (!whatIfUnlocked) return;
                                                            const val = e.target.value;
                                                            setWhatIfInputs(prev => ({ ...prev, moreIncome: val === '' ? '' : parseInt(val) || '' }));
                                                        }}
                                                        placeholder="10000"
                                                        disabled={!whatIfUnlocked}
                                                        className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg disabled:opacity-60 disabled:bg-gray-100"
                                                        style={{ fontSize: '16px' }}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Change Annual Fees (% pts):
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.1"
                                                        value={whatIfInputs.annualFees}
                                                        onChange={(e) => {
                                                            if (!whatIfUnlocked) return;
                                                            const val = e.target.value;
                                                            setWhatIfInputs(prev => ({ ...prev, annualFees: val === '' ? '' : parseFloat(val) || '' }));
                                                        }}
                                                        placeholder="-0.5 (negative = lower)"
                                                        disabled={!whatIfUnlocked}
                                                        className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg disabled:opacity-60 disabled:bg-gray-100"
                                                        style={{ fontSize: '16px' }}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                                                        Sequence Risk (% return for 3 yrs):
                                                    </label>
                                                    <input
                                                        type="number"
                                                        value={whatIfInputs.sequenceRisk}
                                                        onChange={(e) => {
                                                            if (!whatIfUnlocked) return;
                                                            const val = e.target.value;
                                                            setWhatIfInputs(prev => ({ ...prev, sequenceRisk: val === '' ? '' : parseInt(val) || '' }));
                                                        }}
                                                        placeholder="-20 (negative = crash)"
                                                        disabled={!whatIfUnlocked}
                                                        className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg disabled:opacity-60 disabled:bg-gray-100"
                                                        style={{ fontSize: '16px' }}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        
                                        <div className="space-y-3">
                                            {/* Live Longer */}
                                            {whatIfUnlocked && whatIfInputs.liveToAge !== '' && (() => {
                                                const targetAge = parseInt(whatIfInputs.liveToAge, 10) || 100;
                                                const baseline = getBasicBaselineMSI();
                                                let result;
                                                try {
                                                    result = runBasicLiveToAgeWhatIf(getBasicWhatIfContext(), targetAge, baseline);
                                                } catch (_) {
                                                    return null;
                                                }
                                                return (
                                                    <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4">
                                                        <div className="text-sm font-bold text-purple-900 mb-1">📅 Live to Age {targetAge}</div>
                                                        <div className="text-xs text-purple-700">
                                                            Maximum income: ${result.avgIncome.toLocaleString()}/year
                                                            {result.impact !== 0 && (
                                                                <> ({result.impact >= 0 ? '+' : ''}${result.impact.toLocaleString()} vs current plan)</>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            
                                            {/* More Income - EXACT desktop logic */}
                                            {whatIfUnlocked && whatIfInputs.moreIncome !== '' && (() => {
                                                const extraIncome = parseInt(whatIfInputs.moreIncome, 10) || 10000;
                                                const baseline = getBasicBaselineMSI();
                                                const result = runBasicMoreIncomeWhatIf(getBasicWhatIfContext(), extraIncome, baseline);
                                                const targetIncome = baseline + extraIncome;
                                                return (
                                                    <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
                                                        <div className="text-sm font-bold text-green-900 mb-1">💰 What if I need ${targetIncome.toLocaleString()}/year?</div>
                                                        {result.planSucceeds ? (
                                                            <div className="text-xs text-green-700">
                                                                ✓ Your plan succeeds with current balance
                                                            </div>
                                                        ) : (
                                                            <div className="text-xs text-red-700">
                                                                ✗ Need ${result.requiredBalance.toLocaleString()} in super<br/>
                                                                Current balance lasts {result.yearsLastedIsEstimate ? '~' : ''}{result.yearsLasted} years{result.yearsLastedIsEstimate ? ' (estimate)' : ''}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            
                                            {/* Change Annual Fees - EXACT desktop logic */}
                                            {whatIfUnlocked && whatIfInputs.annualFees !== '' && (() => {
                                                const feeDeltaPctPts = parseFloat(whatIfInputs.annualFees) || -0.5;
                                                const baseline = getBasicBaselineMSI();
                                                const ctx = getBasicWhatIfContext();
                                                const result = runBasicAnnualFeesWhatIf(ctx, feeDeltaPctPts, baseline);
                                                const currentFeesDisplay = ((ctx.investmentFees || 0) * 100).toFixed(2);
                                                const newFeesDisplay = Math.max(0, (ctx.investmentFees || 0) * 100 + feeDeltaPctPts).toFixed(2);
                                                return (
                                                    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                                                        <div className="text-sm font-bold text-blue-900 mb-1">💸 What if annual fees change by {feeDeltaPctPts >= 0 ? '+' : ''}{feeDeltaPctPts}%?</div>
                                                        <div className="text-xs text-blue-700">
                                                            Current fees: {currentFeesDisplay}% → New: {newFeesDisplay}%<br/>
                                                            Income would be: ${result.maxSustainableIncome.toLocaleString()}/year<br/>
                                                            Impact: {result.impact >= 0 ? '+' : ''}${result.impact.toLocaleString()}/year
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                            
                                            {/* Sequence Risk - EXACT desktop logic */}
                                            {whatIfUnlocked && whatIfInputs.sequenceRisk !== '' && (() => {
                                                const sequenceRiskPct = parseInt(whatIfInputs.sequenceRisk, 10) || -20;
                                                const baseline = getBasicBaselineMSI();
                                                const result = runBasicSequenceRiskWhatIf(getBasicWhatIfContext(), sequenceRiskPct, baseline);
                                                return (
                                                    <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                                                        <div className="text-sm font-bold text-red-900 mb-1">⚠️ What if markets return {sequenceRiskPct}% for 3 years?</div>
                                                        <div className="text-xs text-red-700">
                                                            Balance after 3 years: ${result.balanceAfter3Years.toLocaleString()}<br/>
                                                            Income would be: ${result.maxSustainableIncome.toLocaleString()}/year<br/>
                                                            Impact: {result.impact >= 0 ? '+' : ''}${result.impact.toLocaleString()}/year
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                    );
                                })()}
                            </div>
                            
                            {/* Modal Footer */}
                            <div className="p-4 border-t bg-gray-50 rounded-b-xl">
                                <button 
                                    onClick={() => setShowDetailedChart(false)}
                                    className="w-full py-3 bg-purple-600 text-white font-bold rounded-lg active:bg-purple-700"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Sticky Calculate / New Calculation Button - always visible */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30 px-3 pt-2"
                 style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 10px)', boxShadow: '0 -4px 16px rgba(0,0,0,0.12)' }}>
                <button
                    onClick={hasCalculated ? () => {
                        setHasCalculated(false);
                        setResults(null);
                        setShowInputsRevertModal(false);
                        setExpandedSection(0);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                    } : handleCalculate}
                    className={`w-full py-3.5 text-white font-bold text-base rounded-xl transition flex items-center justify-center gap-2 min-h-[48px] ${
                        hasCalculated
                            ? 'bg-blue-600 active:bg-blue-700'
                            : 'bg-green-600 active:bg-green-700'
                    }`}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    {hasCalculated ? 'New Calculation' : 'Run calculation'}
                </button>
            </div>
        </div>
    );
};

export default BasicCalculatorMobile;

